import { useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { RestartAlt, Send } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectAuth } from "../../auth/authSlice";
import VoiceInputButton from "../../../components/VoiceInputButton";
import { VoiceLanguage } from "../../../hooks/useVoiceInput";
import { Employee } from "../../process/types/processTypes";
import { Company } from "../../setups/types/setupTypes";
import { createWallPost, editWallPost } from "../wallSlices";
import { WALL_CATEGORIES } from "../wallConstants";
import { employeeLabel, searchCompanies, searchEmployees } from "../wallSearch";
import {
  BrokerInput,
  WallCategory,
  WallPost,
  WallUser,
} from "../types/wallTypes";

type BrokerOption = Company | string;

type Props = {
  officeUsers: WallUser[];
  editingPost?: WallPost | null;
  onDone?: () => void;
};

export default function PostComposer({
  officeUsers,
  editingPost,
  onDone,
}: Props) {
  const { t, i18n } = useTranslation();
  const { t: te } = useTranslation("enums");

  const voiceLanguage: VoiceLanguage =
    i18n.language === "ar"
      ? "ar"
      : i18n.language === "am" || i18n.language === "et"
        ? "am"
        : "en";
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);

  const [message, setMessage] = useState("");
  const [category, setCategory] = useState<WallCategory>(WallCategory.General);
  const [tags, setTags] = useState<string[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [contacts, setContacts] = useState<string[]>([]);
  const [mentions, setMentions] = useState<WallUser[]>([]);

  const [employeeOptions, setEmployeeOptions] = useState<Employee[]>([]);
  const [companyOptions, setCompanyOptions] = useState<Company[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!editingPost) return;
    setMessage(editingPost.message);
    setCategory(editingPost.category);
    setTags(editingPost.tags ?? []);
    setEmployees(editingPost.employees ?? []);
    setBrokers(
      (editingPost.brokers ?? []).map((b) =>
        b.company ? (b.company as Company) : (b.name ?? ""),
      ),
    );
    setContacts((editingPost.contacts ?? []).map((c) => c.name));
    setMentions(editingPost.mentions ?? []);
  }, [editingPost]);

  const reset = () => {
    setMessage("");
    setCategory(WallCategory.General);
    setTags([]);
    setEmployees([]);
    setBrokers([]);
    setContacts([]);
    setMentions([]);
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    const brokerInputs: BrokerInput[] = brokers.map((b) =>
      typeof b === "string" ? { name: b } : { companyId: b._id },
    );
    const input = {
      message: message.trim(),
      category,
      tags,
      employeeIds: employees.map((e) => e._id!).filter(Boolean),
      brokers: brokerInputs,
      contacts: contacts.map((name) => ({ name })),
      mentionIds: mentions.map((m) => m._id!).filter(Boolean),
    };
    if (editingPost?._id) {
      await dispatch(editWallPost({ id: editingPost._id, ...input }));
    } else {
      await dispatch(createWallPost(input));
      reset();
    }
    setSubmitting(false);
    onDone?.();
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 3 },
  };

  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0px 6px 24px rgba(83, 109, 254, 0.08)",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="flex-start" spacing={1.5}>
            <Avatar
              src={user?.avatar}
              sx={{
                width: 44,
                height: 44,
                bgcolor: "primary.main",
                fontWeight: 600,
              }}
            >
              {user?.name?.[0]?.toUpperCase()}
            </Avatar>
            <TextField
              label={t("writeSomething")}
              placeholder={t("writeSomething") as string}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "action.hover",
                },
              }}
            />
            {/* <VoiceInputButton
              language={voiceLanguage}
              onTranscript={(text) =>
                setMessage((prev) => (prev ? `${prev} ${text}` : text))
              }
            /> */}
          </Stack>
          {/* <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              label={t("category")}
              value={category}
              onChange={(e) => setCategory(e.target.value as WallCategory)}
              sx={{ minWidth: 200 }}
            >
              {WALL_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {te(`wallCategory.${c}`)}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={tags}
              onChange={(_, v) => setTags(v as string[])}
              sx={{ flex: 1, minWidth: 200 }}
              renderInput={(params) => (
                <TextField {...params} label={t("tags")} />
              )}
            />
          </Stack> */}

          {/* <Autocomplete
            multiple
            freeSolo
            options={companyOptions}
            value={brokers}
            filterOptions={(x) => x}
            getOptionLabel={(o) =>
              typeof o === "string" ? o : (o as Company).displayName
            }
            isOptionEqualToValue={(o, v) =>
              typeof o !== "string" && typeof v !== "string" && o._id === v._id
            }
            onInputChange={async (_, val) => {
              if (val.length >= 2)
                setCompanyOptions(await searchCompanies(val));
            }}
            onChange={(_, v) => setBrokers(v as BrokerOption[])}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("brokers")}
                helperText={t("brokerHelper")}
              />
            )}
          /> */}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            {/* <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={contacts}
              onChange={(_, v) => setContacts(v as string[])}
              sx={{ flex: 1, minWidth: 200 }}
              renderInput={(params) => (
                <TextField {...params} label={t("contacts")} />
              )}
            /> */}
            <Autocomplete
              multiple
              options={employeeOptions}
              value={employees}
              sx={{ flex: 1, minWidth: 200, ...fieldSx }}
              filterOptions={(x) => x}
              getOptionLabel={(o) => employeeLabel(o as Employee)}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              onInputChange={async (_, val) => {
                if (val.length >= 2)
                  setEmployeeOptions(await searchEmployees(val));
              }}
              onChange={(_, v) => setEmployees(v as Employee[])}
              renderInput={(params) => (
                <TextField {...params} label={t("linkEmployees")} />
              )}
            />
            <Autocomplete
              multiple
              options={officeUsers}
              value={mentions}
              getOptionLabel={(o) => (o as WallUser).name ?? ""}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              onChange={(_, v) => setMentions(v as WallUser[])}
              sx={{ flex: 1, minWidth: 200, ...fieldSx }}
              renderInput={(params) => (
                <TextField {...params} label={t("mention")} />
              )}
            />
          </Stack>

          <Divider sx={{ mx: -2.5 }} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={reset}
              disabled={submitting}
              color="inherit"
              startIcon={<RestartAlt sx={{ fontSize: 18 }} />}
              sx={{
                mr: "auto",
                borderRadius: 99,
                textTransform: "none",
                px: 3,
                color: "text.secondary",
              }}
            >
              {t("reset")}
            </Button>
            {editingPost && (
              <Button
                onClick={onDone}
                disabled={submitting}
                sx={{ borderRadius: 99, textTransform: "none", px: 3 }}
              >
                {t("cancel")}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !message.trim()}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Send sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                borderRadius: 99,
                textTransform: "none",
                px: 3.5,
                fontWeight: 600,
                boxShadow: (theme) =>
                  `0 6px 16px ${theme.palette.primary.main}40`,
              }}
            >
              {editingPost ? t("editPost") : t("postUpdate")}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
